import { CommonDialogs } from "@mcleod/common";
import { BlurEvent, Button, DataSourceMode, EditRowDecorator, Snackbar, Textbox, Toast, TreeNode } from "@mcleod/components";
import { Api, StringUtil } from "@mcleod/core";
import { AutogenLayoutResponsibilityManageHierarchy } from "./autogen/AutogenLayoutResponsibilityManageHierarchy";
import { ResponsibilityUtil } from "./ResponsibilityUtil";

export class ResponsibilityManageHierarchy extends AutogenLayoutResponsibilityManageHierarchy {

	/**
	 * Show the Add Hierarchy editor for a given level and parent.
	 * This encapsulates the EditRowDecorator creation that was previously in ResponsibilityControl.
	 */
	public showAddHierarchy(level: number, parentId?: string, description?: string, refreshTree?: boolean, populateTree?: () => Promise<void> | void) {
		const title = "Add " + (description || "");
		new EditRowDecorator({
			title: title,
			layout: this, fillVerticalSpace: true, width: '50%',
			overlayProps: { greyedBackground: true, closeOnClickOff: false },
			layoutLoadListeners: async () => {
				this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await this.mainDataSource.createBlankRow()]);
				this.activeRow.set("hierarchy_level", level);
				this.activeRow.set("parent_id", parentId);
				this.textboxId.caption = description || "";
				if (level === 1) {
					this.textboxId.setProps(ResponsibilityUtil.getAgencyLookup());
				} else {
					this.textboxId.setProps(ResponsibilityUtil.getDefaultFreeform());
				}
			},
			addlComponents: this.addHierarchyViaTree(level, true, refreshTree, populateTree),
            onClose: async (cancelled: boolean) => {
                this.handleUnsavedClose(cancelled);
            },
		});
	}

	/**
	 * Show the Update Hierarchy editor for a given level and existing ModelRow.
	 */
	public showUpdateHierarchy(level: number, modelRow: any, description?: string, refreshTree?: boolean, populateTree?: () => Promise<void> | void) {
		const title = "Update " + (description || "") + " - " + (modelRow?.get ? modelRow.get("id") : modelRow?.id);
		new EditRowDecorator({
			title: title,
			layout: this, fillVerticalSpace: true, width: '50%',
			overlayProps: { greyedBackground: true, closeOnClickOff: false },
			layoutLoadListeners: async () => {
				this.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, [modelRow]);
				this.textboxId.caption = description;
			},
			addlComponents: this.addHierarchyViaTree(level, false, refreshTree, populateTree),
            onClose: async (cancelled: boolean) => {
                this.handleUnsavedClose(cancelled);
            },
		});
	}

	/**
	 * Create the Add/Update button used by the Add Hierarchy editor.
	 * This was previously a standalone function in ResponsibilityControl.
	 */
	public addHierarchyViaTree(level: number, isAddUpdate: boolean, refreshTree: boolean, populateTree?: () => Promise<void> | void): Button {
		const caption = isAddUpdate ? "Add" : "Update";
		const addButton = new Button({
			caption: caption,
			backgroundColor: "primary",
			borderColor: "primary",
			borderWidth: 1,
			color: "primary.reverse",
			rowBreak: false,
			onClick: async () => {
				try {
					addButton.busy = true;
					addButton.enabled = false;
					const isDataSourceChanged = this.mainDataSource.hasChanged();
					if (isDataSourceChanged === false) {
						CommonDialogs.showError("No changes were made. Please try again or check your input.");
						return;
					}
					const hierarchyId = this.activeRow.get("id");
					const hierarchyDescription = this.activeRow.get("description");

					if (!hierarchyId) {
						CommonDialogs.showError("Hierarchy ID is required to save the record.");
						this.textboxId.focus();
						return;
					}

					if (!hierarchyDescription) {
						CommonDialogs.showError("Hierarchy Description is required to save the record.");
						return;
					}

                    const confirmMessage = isAddUpdate
                        ? `Are you sure you want to Add Hierarchy with ID: ${hierarchyId}?`
                        : `Are you sure you want to Update Hierarchy?`;
                    const title = `${isAddUpdate ? "Add" : "Update"} Hierarchy`;
                    const props = { yesButtonCaption: isAddUpdate ? "Add" : "Update", noButtonCaption: "Cancel" };

                    const confirm = await CommonDialogs.showYesNo(confirmMessage, title, props);
					if (confirm !== true) {
						return;
					}
					const response = await this.mainDataSource.post();
					if (response) {
						const message = isAddUpdate ? "Hierarchy Added Successfully." : "Hierarchy Updated Successfully.";
                        if (isAddUpdate && level == 1) {
                            this.textboxId.lookupModel = null;
                            this.textboxId.lookupModelLayout = null;
                        }
						Toast.showToast(message);
						await this.delay(2000);
						this.slideOut();
						if (refreshTree) {
							await populateTree();
						}
					}
				} catch (error) {
					CommonDialogs.showError(error);
				}
				finally {
					addButton.busy = false;
					addButton.enabled = true;
				}
			}
		});
		return addButton;
	}

	private delay(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	/**
	 * Delete a hierarchy item after validating it has no real children.
	 */
	public async deleteHierarchy(node: TreeNode, item: any, refreshTree?: boolean, populateTree?: () => Promise<void> | void): Promise<void> {

        const mainHierarchyId: string = item.id;
        if (node == null || StringUtil.isEmptyString(mainHierarchyId)) {
			CommonDialogs.showError("Select the hierarchy item to delete");
			return;
		}

        const placeholderText = ResponsibilityUtil.getPlaceHolderText();
        const deleteHierarchyIDs: string[] = [mainHierarchyId];

        const allChildrenNodesWithPlaceHolder: TreeNode[] = node.getRecursiveChildNodes() ?? [];
        const allChildrenNodes: TreeNode[] = allChildrenNodesWithPlaceHolder.filter((child: any) => child.text?.trim() !== placeholderText);
        const hierarchyChildrenIDs = this.getHierarchyIDFromChildrenNodes(allChildrenNodes);
        deleteHierarchyIDs.push(...hierarchyChildrenIDs);

        let dialogMessage = `Are you sure you want to delete hierarchy "${mainHierarchyId}"?`;
        if (allChildrenNodes.length > 0) {
            const subHierarchyList = hierarchyChildrenIDs.map((id, index) => `${index + 1}. ${id}`).join('\n');
            dialogMessage += `\n\nThis action will also delete the following associated sub-hierarchies:\n${subHierarchyList}`;
        }
        const confirm = await CommonDialogs.showYesNo(dialogMessage, "Delete Confirmation", { yesButtonCaption: "Delete", noButtonCaption: "Cancel"});
        if (confirm !== true) {
            return;
        }

        const response = await Api.delete("lme/general/delete-hierarchy-node", {delete_hierarchy_ids: deleteHierarchyIDs});
        const data = response?.data?.[0];
        const success = data?.success;
        const message = success ? "Hierarchy Deleted Successfully" : "Failed to delete the Hierarchy.";
        Toast.showToast(message);

        if (refreshTree) {
            await populateTree();
        }

	}

    private getHierarchyIDFromChildrenNodes(allChildrenNodes: TreeNode[]): string[] {
        const hierarchyIds: string[] = [];

        for (const child of allChildrenNodes) {
            const text: string = child?.text?.trim() ?? "";

            if (!text) {
                continue;
            }

            const parts = text.split(":");
            if (parts.length >= 2) {
                const hierarchyId = parts[1].trim();
                hierarchyIds.push(hierarchyId);
            }
        }
        return hierarchyIds;
    }

    async textboxIdOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const lookupModelDataHierarchy = textbox.lookupModelData;
        let hierarchyId: string;
        if (lookupModelDataHierarchy == null) {
            hierarchyId = textbox.text;
        } else {
            const data = lookupModelDataHierarchy[0];
            if (data) {
                hierarchyId = data.get("id");
            }
        }
        if (!StringUtil.isEmptyString(hierarchyId)) {
            const response = await Api.search("lme/general/validate-hierarchy", { "operation": "Hierarchy", "hierarchy_id": hierarchyId });
            const data = response?.data[0];
            if (data) {
                const hierarchyExist = data.hierarchy_exists;
                if (hierarchyExist){
                    this.activeRow.set({"id": null});
                    Snackbar.showWarningSnackbar("The ID \"" + hierarchyId + "\" already exists in 'hierarchy' table.", {millisUntilDismissal: 10000});
                    this.textboxId.focus();
                }
            }
        }
    }

    private async handleUnsavedClose(cancelled: boolean) {
        if (!this.mainDataSource.hasChanged()) {
            this.slideOut();
            this.activeRow.set({ id: null, hierarchy_level: null, parent_id: null, description: null });
            return;
        }

        const message = "Unsaved changes are present. Do you wish to leave without saving changes?";
        const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
        const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
        if (!leave) return;

        this.slideOut();
        this.activeRow.set({ id: null, hierarchy_level: null, parent_id: null, description: null });
    }

}
